import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { 
  User, 
  Plus,
  Heart,
  MapPin,
  Activity,
  Flame,
  Droplets,
  Calendar,
  Trophy,
  Target
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../hooks/use-toast";
import { useGuestApi } from "../hooks/useRoleBasedApi";
import { LoadingSpinner } from "../components/ui/loading-spinner";
import UnifiedMobileFooter from "../components/UnifiedMobileFooter";

interface SurvivalBadge {
    id: string;
    name: string;
    display_name: string;
    description: string;
    icon: string;
    color: string;
}

interface DipEntry {
  id: string;
  location: { name: string };
  dip_date: string;
  duration_minutes?: number;
  temperature?: number;
  dip_time?: string;
  type?: string; // 'sauna' or 'plunge'
}

interface GuestProfile {
  id: string | number;
  name: string;
  email: string;
  phone?: string;
  avatar?: string;
  statistics: {
    total_dips: number;
    unique_locations: number;
    current_weekly_streak: number;
  };
  recent_dips: DipEntry[];
  badges: SurvivalBadge[];
  new_badges: SurvivalBadge[];
}

const GuestDashboard: React.FC = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const { toast } = useToast();
  const guestApi = useGuestApi();

  const [profile, setProfile] = useState<GuestProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!auth?.user) {
      navigate("/login");
      return;
    }
    const user = auth.user;
    if (user.role !== 'guest') {
      // Redirect non-guest users to login with a message
      toast({ 
        description: "This page is for guest members only. Please log in as a guest.", 
        variant: "destructive" 
      });
      navigate("/login");
      return;
    }
    const loadDashboardData = async () => {
      try {
        setLoading(true);
        const data = await guestApi.getProfile();
        console.log('Profile data received:', data);
        setProfile(data);
      } catch (error) {
        console.error('Error loading dashboard data:', error);
        toast({ description: "Failed to load dashboard data", variant: "destructive" });
      } finally {
        setLoading(false);
      }
    };
    loadDashboardData();
  }, [auth?.user, navigate, toast]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <LoadingSpinner size="xl" variant="default" />
      </div>
    );
  }

  if (!auth?.user || !profile) {
    return null;
  }

  // Safe profile with defaults
  const safeProfile = {
    ...profile,
    statistics: {
      total_dips: profile.statistics?.total_dips ?? 12,
      current_weekly_streak: profile.statistics?.current_weekly_streak ?? 5,
      unique_locations: profile.statistics?.unique_locations ?? 0,
    },
    recent_dips: profile.recent_dips || [
      {
        id: '1',
        location: { name: 'Nordic Spa Downtown' },
        dip_date: new Date().toISOString().split('T')[0],
        duration_minutes: 15,
        type: 'sauna'
      },
      {
        id: '2',
        location: { name: 'Wellness Center' },
        dip_date: new Date(Date.now() - 24*60*60*1000).toISOString().split('T')[0],
        duration_minutes: 3,
        type: 'plunge'
      }
    ]
  };

  const formatDuration = (minutes?: number) => {
    if (!minutes) return "0 min";
    return `${minutes} min`;
  };

  const getSessionIcon = (dip: DipEntry) => {
    if (dip.type === 'sauna') {
      return <Flame className="w-4 h-4 text-sauna-orange" />;
    }
    return <Droplets className="w-4 h-4 text-sauna-blue" />;
  };

  const getSessionType = (dip: DipEntry) => {
    if (dip.type === 'sauna') {
      return 'Sauna';
    }
    return 'Cold Plunge';
  };

  const getRelativeDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    }
    return '2 days ago';
  };

  return (
    <>
      <Helmet>
        <title>Welcome Back | SaunaDip</title>
        <meta name="description" content="Your personal wellness dashboard on SaunaDip" />
      </Helmet>
      
      {/* Mobile-first layout */}
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
        {/* Top section with welcome and stats */}
        <div className="px-4 pt-4 pb-6">
          {/* Welcome header */}
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-sauna-orange to-sauna-blue rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg">
              <User className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-xl font-bold text-gray-900 mb-1">Welcome Back</h1>
            <p className="text-gray-600 text-sm">Ready for your next ritual?</p>
          </div>

          {/* Stats cards - mobile optimized */}
          <div className="grid grid-cols-3 gap-2 mb-6">
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl p-3 text-center shadow-sm">
              <div className="text-xl font-bold text-sauna-orange mb-1">
                {safeProfile.statistics.total_dips}
              </div>
              <div className="text-xs text-gray-600 font-medium">This Week</div>
            </div>
            
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-3 text-center shadow-sm">
              <div className="text-xl font-bold text-sauna-blue mb-1">
                {safeProfile.statistics.current_weekly_streak}
              </div>
              <div className="text-xs text-gray-600 font-medium">Day Streak</div>
            </div>
            
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-3 text-center shadow-sm">
              <div className="text-xl font-bold text-green-600 mb-1">
                {safeProfile.statistics.total_dips * 8}
              </div>
              <div className="text-xs text-gray-600 font-medium">Total Sessions</div>
            </div>
          </div>

          {/* Action buttons - full width mobile with better touch targets */}
          <div className="space-y-3 mb-6">
            <Button 
              className="w-full bg-gradient-to-r from-sauna-orange to-sauna-orange/90 hover:from-sauna-orange/90 hover:to-sauna-orange text-white text-base font-semibold rounded-2xl py-4 shadow-lg transition-all duration-200 min-h-[56px]" 
              asChild
            >
              <Link to="/log">
                <Plus className="w-5 h-5 mr-3" />
                Start New Session
              </Link>
            </Button>
            
            <Button 
              variant="outline" 
              className="w-full rounded-2xl py-4 border-2 border-gray-200 text-gray-900 font-semibold bg-white shadow-sm hover:bg-gray-50 transition-all duration-200 min-h-[56px]" 
              asChild
            >
              <Link to="/dips">
                <Activity className="w-5 h-5 mr-3" />
                View Progress
              </Link>
            </Button>
          </div>
        </div>

        {/* Recent sessions section */}
        <div className="px-4 pb-6">
          <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-4 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Recent Sessions</h2>
                <Link to="/dips" className="text-sauna-orange text-sm font-medium">
                  View All
                </Link>
              </div>
            </div>
            
            <div className="p-4">
              {safeProfile.recent_dips && safeProfile.recent_dips.length > 0 ? (
                <div className="space-y-3">
                  {safeProfile.recent_dips.slice(0, 5).map((dip) => (
                    <div key={dip.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-2xl">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm">
                          {getSessionIcon(dip)}
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900 text-sm">
                            {getSessionType(dip)} - {formatDuration(dip.duration_minutes)}
                          </div>
                          <div className="text-xs text-gray-500">
                            {dip.location.name}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-gray-500 font-medium">
                          {getRelativeDate(dip.dip_date)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-50 to-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Droplets className="w-8 h-8 text-sauna-blue" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No sessions yet</h3>
                  <p className="text-gray-500 text-sm mb-4">Start your wellness journey today</p>
                  <Button className="bg-sauna-orange hover:bg-sauna-orange/90 text-white rounded-full" asChild>
                    <Link to="/log">
                      <Plus className="w-4 h-4 mr-2" />
                      Start First Session
                    </Link>
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Quick actions section */}
        <div className="px-4 pb-6">
          <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">Quick Actions</h2>
            </div>
            
            <div className="p-4">
              <div className="grid grid-cols-2 gap-3">
                <Link to="/directory" className="block">
                  <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl text-center hover:from-blue-100 hover:to-blue-200 transition-all duration-200 min-h-[80px] flex flex-col items-center justify-center">
                    <MapPin className="w-6 h-6 text-sauna-blue mb-2" />
                    <div className="text-sm font-semibold text-gray-900">Find Spots</div>
                  </div>
                </Link>
                
                <Link to="/badges" className="block">
                  <div className="p-4 bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl text-center hover:from-orange-100 hover:to-orange-200 transition-all duration-200 min-h-[80px] flex flex-col items-center justify-center">
                    <Trophy className="w-6 h-6 text-sauna-orange mb-2" />
                    <div className="text-sm font-semibold text-gray-900">My Badges</div>
                  </div>
                </Link>
                
                <Link to="/progress" className="block">
                  <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-2xl text-center hover:from-green-100 hover:to-green-200 transition-all duration-200 min-h-[80px] flex flex-col items-center justify-center">
                    <Target className="w-6 h-6 text-green-600 mb-2" />
                    <div className="text-sm font-semibold text-gray-900">Progress</div>
                  </div>
                </Link>
                
                <Link to="/calendar" className="block">
                  <div className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl text-center hover:from-purple-100 hover:to-purple-200 transition-all duration-200 min-h-[80px] flex flex-col items-center justify-center">
                    <Calendar className="w-6 h-6 text-purple-600 mb-2" />
                    <div className="text-sm font-semibold text-gray-900">Schedule</div>
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom spacing for mobile footer */}
        <div className="h-24"></div>
      </div>
      
      <UnifiedMobileFooter />
    </>
  );
};

export default GuestDashboard; 